import { BEST_HAND_BET_AMOUNT } from '../constants/config';

/**
 * Calculate side bet net winnings for a player result
 * Handles both old format (won: true) and new format (winnings field)
 */
const calculateSideBetNet = (result, allResults) => {
  let totalWinnings = 0;
  let totalCost = 0;

  // Handle legacy format
  if (
    result.bestHandParticipant !== undefined ||
    result.bestHandWinner !== undefined
  ) {
    const participated = Boolean(result.bestHandParticipant);
    const won = Boolean(result.bestHandWinner);

    if (participated) {
      totalCost += BEST_HAND_BET_AMOUNT;
    }

    if (won) {
      const participants = allResults.filter((r) =>
        Boolean(r.bestHandParticipant)
      );
      const winners = allResults.filter((r) => Boolean(r.bestHandWinner));
      const pot = participants.length * BEST_HAND_BET_AMOUNT;
      totalWinnings += winners.length > 0 ? pot / winners.length : 0;
    }

    return { totalWinnings, totalCost, net: totalWinnings - totalCost };
  }

  // Handle sideBets array format
  if (result.sideBets && Array.isArray(result.sideBets)) {
    result.sideBets.forEach((sideBet) => {
      const timesParticipated =
        sideBet.timesParticipated || (sideBet.participated ? 1 : 0);
      const amount = sideBet.amount || 0;
      const cost = timesParticipated * amount;
      totalCost += cost;

      // Check if this has manual winnings (new format) or needs calculation
      if (sideBet.winnings !== undefined && sideBet.winnings !== null) {
        totalWinnings += sideBet.winnings;
      } else if (sideBet.won) {
        // Old format: calculate winnings from pot
        let participantsCount = 0;
        let winnersCount = 0;

        allResults.forEach((r) => {
          if (r.sideBets && Array.isArray(r.sideBets)) {
            const otherSideBet = r.sideBets.find(
              (sb) => sb.sideBetId === sideBet.sideBetId
            );
            if (otherSideBet) {
              const tp =
                otherSideBet.timesParticipated ||
                (otherSideBet.participated ? 1 : 0);
              if (tp > 0) participantsCount += tp;
              if (otherSideBet.won && tp > 0) winnersCount += tp;
            }
          }
        });

        const pot = participantsCount * amount;
        const playerShare =
          winnersCount > 0 ? (timesParticipated / winnersCount) * pot : 0;
        totalWinnings += playerShare;
      }
    });
  }

  return { totalWinnings, totalCost, net: totalWinnings - totalCost };
};

/**
 * Export games to CSV with all player stats
 * @param {Array} games - Array of games to export
 * @param {Object} selectedGroup - Currently selected group
 * @param {Array} groupUsers - Array of group users for name lookup
 */
export const exportGamesToCSV = (games, selectedGroup, groupUsers = []) => {
  console.warn('\n=== CSV EXPORT ===');
  console.warn(`Exporting ${games?.length || 0} games`);
  console.warn('Game dates:', games?.map((g) => g.date).join(', '));

  if (!games || games.length === 0) {
    alert('No games to export');
    return;
  }

  // Create user lookup map
  const userLookup = {};
  groupUsers.forEach((user) => {
    userLookup[user.userId] = user;
  });

  // Build CSV rows
  const rows = [];

  // Header row
  rows.push([
    'Game Date',
    'Location',
    'Game Type',
    'Buy-in',
    'Player Name',
    'Position',
    'Tournament Winnings',
    'Rebuys',
    'Side Bet Cost',
    'Side Bet Winnings',
    'Side Bet Net',
    'Total Winnings',
    'Total Costs',
    'Net P&L',
  ]);

  // Sort games by date (newest first)
  const sortedGames = [...games].sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  });

  // Process each game
  sortedGames.forEach((game) => {
    const gameBuyin = game.buyin || 20;
    const results = game.results || [];

    // Sort results by position
    const sortedResults = [...results].sort((a, b) => {
      return (a.position || 999) - (b.position || 999);
    });

    // Add row for each player
    sortedResults.forEach((result) => {
      const user = userLookup[result.userId];
      const playerName = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.displayName ||
          user.email ||
          'Unknown'
        : `Unknown (${result.userId?.slice(-8) || 'N/A'})`;

      const tournamentWinnings = result.winnings || 0;
      const rebuys = result.rebuys || 0;
      const gameCosts = gameBuyin + rebuys * gameBuyin;

      // Calculate side bet winnings/costs
      const sideBetResult = calculateSideBetNet(result, results);

      const totalWinnings = tournamentWinnings + sideBetResult.totalWinnings;
      const totalCosts = gameCosts + sideBetResult.totalCost;
      const netPL = totalWinnings - totalCosts;

      rows.push([
        game.date,
        game.location || '-',
        game.gameType || 'Tournament',
        `$${gameBuyin}`,
        playerName,
        result.position || '-',
        `$${tournamentWinnings.toFixed(2)}`,
        rebuys,
        `$${sideBetResult.totalCost.toFixed(2)}`,
        `$${sideBetResult.totalWinnings.toFixed(2)}`,
        `$${sideBetResult.net.toFixed(2)}`,
        `$${totalWinnings.toFixed(2)}`,
        `$${totalCosts.toFixed(2)}`,
        `$${netPL.toFixed(2)}`,
      ]);
    });
  });

  // Convert to CSV string
  const csvContent = rows
    .map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap in quotes if contains comma or quote
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(',')
    )
    .join('\n');

  // Create download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  const fileName = selectedGroup
    ? `${selectedGroup.name.replace(/[^a-z0-9]/gi, '_')}_games_export_${new Date().toISOString().split('T')[0]}.csv`
    : `poker_games_export_${new Date().toISOString().split('T')[0]}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
